import { useAtomValue, useSetAtom } from 'jotai';
import { ComponentProps, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { MeasureConnected, MeasureResponseCompletionCampaign } from '@grow-therapy-team/seedling-components';

import { PageComponentProps } from '../../components/PaginatedDrawer';
import { MeasuresActionsWrapper as BaseMeasuresActions } from '../../components/measures/MeasuresActionsWrapper';
import { MeasuresContent as BaseMeasuresContent } from '../../components/measures/MeasuresContent';
import { MeasuresDrawerLoadingSkeleton } from '../../components/measures/MeasuresDrawer';
import { PendingMeasuresForm } from '../../components/measures/PendingMeasuresForm';
import { MeasureSchemaAndAnswers } from '../../components/measures/types';
import {
  VisitorState,
  hasCompletedClientMeasuresAtom,
  visitorMeasuresAtom,
  visitorMeasuresPageIndexAtom,
  visitorStateAtom,
} from '../state';
import { useRecordClientMeasure } from '../useRecordClientMeasure';
import { usePreSessionPromptState } from '../waiting-room/pre-session-prompt/usePreSessionPromptState';
import { AppointmentStatus, YoureAllSetForYourAppointmentCard } from './YoureAllSetForYourAppointmentCard';
import { buildMeasurePages } from './helpers';
import { useAutoCloseMeasuresDrawer } from './useAutoCloseMeasuresDrawer';
import { useGetClientUserShortId } from './useGetClientUserShortId';
import { useTrackMeasureStarted } from './useTrackMeasureStarted';

function Measure(props: ComponentProps<typeof MeasureConnected>) {
  const onValuesChange = useTrackMeasureStarted();
  return <MeasureConnected measureHeadingAs='h3' {...props} onValuesChangeCallback={onValuesChange} />;
}

function MeasuresContent(props: ComponentProps<typeof BaseMeasuresContent>) {
  return <BaseMeasuresContent measureComponent={Measure} {...props} />;
}

function MeasuresActionsWrapper(props: PageComponentProps<MeasureSchemaAndAnswers>) {
  const clientUserShortId = useGetClientUserShortId();
  const { providerShortId } = useParams();
  const { clearPreSessionPrompt } = usePreSessionPromptState();
  const setMeasures = useSetAtom(visitorMeasuresAtom);
  const setHasCompletedClientMeasures = useSetAtom(hasCompletedClientMeasuresAtom);
  const setMeasuresPageIndex = useSetAtom(visitorMeasuresPageIndexAtom);
  const [recordMeasure] = useRecordClientMeasure(providerShortId);
  const handleSuccessfulSubmission = () => {
    if (providerShortId) {
      recordMeasure();
    }
    if (props.isLastPage) {
      setMeasures([]);
      setHasCompletedClientMeasures(true);
      setMeasuresPageIndex(0);
      clearPreSessionPrompt();
    }
  };

  return (
    <BaseMeasuresActions
      {...props}
      clientUserShortId={clientUserShortId}
      providerShortId={providerShortId}
      completionCampaign={MeasureResponseCompletionCampaign.WAITING_ROOM}
      onSubmitMeasureSuccess={handleSuccessfulSubmission}
      errorScrollContainerRef={props.scrollContainerRef}
    />
  );
}

export function ClientMeasuresDrawerWrapper() {
  const measures = useAtomValue(visitorMeasuresAtom);
  const pages = buildMeasurePages(measures);
  const visitorState = useAtomValue(visitorStateAtom);
  const savedPageIndex = useAtomValue(visitorMeasuresPageIndexAtom);
  const setMeasuresPageIndex = useSetAtom(visitorMeasuresPageIndexAtom);

  useAutoCloseMeasuresDrawer();

  const handlePageIndexChange = useCallback(
    (pageIndex: number) => setMeasuresPageIndex(pageIndex),
    [setMeasuresPageIndex],
  );

  const status: AppointmentStatus = visitorState === VisitorState.MEETING ? 'in-session' : 'waiting';

  return (
    <PendingMeasuresForm
      pages={pages}
      headingAs='h3'
      initialPageIndex={savedPageIndex}
      contentComponent={MeasuresContent}
      actionsComponent={MeasuresActionsWrapper}
      drawerBodyProps={{ className: '!py-0' }}
      onPageIndexChange={handlePageIndexChange}
      loadingFallback={<MeasuresDrawerLoadingSkeleton />}
      emptyFallback={<YoureAllSetForYourAppointmentCard status={status} />}
    />
  );
}
